import { useEffect, useRef, useState } from "react";
import { ArrowLeft, MailCheck } from "lucide-react";
import { useLogin } from "@client/hooks/useAuth";
import { ApiError } from "@client/lib/api";
import { SALES_MESSAGES, wa } from "@client/lib/sales";
import { AuthShell } from "@client/components/auth/AuthShell";
import { Field, FormAlert, PasswordField, SubmitButton, TextField } from "@client/components/auth/fields";

/**
 * Where "Falar com o suporte" goes, when it goes anywhere.
 *
 * `VITE_SUPPORT_WHATSAPP` is the support number in international digits ("5584999998888"), set in
 * `.env`. Unset — which is the default — the link does not render at all: a "Fale com o suporte"
 * pointing at a number nobody answers is worse than no link.
 */
const rawSupport = import.meta.env.VITE_SUPPORT_WHATSAPP as string | undefined;
const SUPPORT_WHATSAPP = rawSupport?.replace(/\D/g, "")
  ? `https://wa.me/${rawSupport.replace(/\D/g, "")}?text=${
      encodeURIComponent("Oi! Preciso de ajuda para entrar na minha conta do ChatNutri.")}`
  : null;

const desktop = () => typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches;

/**
 * "Esqueci minha senha", in place of the login form rather than under it.
 *
 * It always says the same thing, whatever the address was. The endpoint answers 204 either way —
 * telling a stranger whether an account exists here is an oracle — and the screen must not undo
 * that by looking different when the address is real.
 */
function ForgotPassword({ initialEmail, onClose }: { initialEmail: string; onClose: () => void }) {
  const [email, setEmail] = useState(initialEmail);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await fetch("/api/public/forgot-password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
    } catch {
      // Even a network failure says the same thing: the alternative is a screen that reveals,
      // by its error, that the request reached us.
    } finally {
      setSent(true);
      setBusy(false);
    }
  };

  const back = (
    <button type="button" onClick={onClose}
            className="mt-6 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-[var(--l-accent-ink)] hover:underline">
      <ArrowLeft className="h-4 w-4" /> Voltar para o login
    </button>
  );

  if (sent) {
    return (
      <div className="text-center">
        <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--l-paper)]">
          <MailCheck className="h-7 w-7 text-[var(--l-accent-ink)]" />
        </span>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Confira o seu e-mail</h1>
        <p className="mt-2 text-sm leading-relaxed text-[var(--l-ink-muted)]">
          Se houver uma conta com esse e-mail, o link para criar uma senha nova já saiu. Ele vale por
          7 dias e só pode ser usado uma vez. Olhe também o spam.
        </p>
        {back}
      </div>
    );
  }
  return (
    <>
      <h1 className="font-display text-2xl font-extrabold tracking-tight">Recuperar acesso</h1>
      <p className="mt-1.5 text-sm text-[var(--l-ink-muted)]">
        Informe o seu e-mail e enviamos um link para criar uma senha nova.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="mt-6 space-y-5">
        <Field id="forgot-email" label="E-mail">
          <TextField id="forgot-email" type="email" autoComplete="email" required value={email} autoFocus
                     onChange={(e) => setEmail(e.target.value)} placeholder="voce@consultorio.com.br" />
        </Field>
        <SubmitButton busy={busy} busyLabel="Enviando…" disabled={email.trim().length < 3}>Enviar o link</SubmitButton>
      </form>
      {back}
    </>
  );
}

export function Login() {
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgot, setForgot] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  /*
   * Every failure says something now.
   *
   * `api.ts` used to replace the server's "E-mail ou senha incorretos." with "Sessão expirada" on
   * any 401, and a network failure threw a bare TypeError that this line dropped on the floor —
   * so offline, submitting did nothing visible at all. Both are fixed at the source; the
   * `instanceof` fallback stays as the last resort rather than the common path.
   */
  const error = login.error
    ? login.error instanceof ApiError
      ? login.error.message
      : "Não conseguimos entrar. Tente de novo."
    : null;

  // On desktop the cursor starts in the e-mail; on a phone it does not, because focusing pops the
  // keyboard over the page before the person has even seen it.
  useEffect(() => { if (desktop()) emailRef.current?.focus(); }, []);
  // After a wrong password, straight back to the password, selected, ready to retype.
  useEffect(() => { if (login.isError) passwordRef.current?.select(); }, [login.isError, login.failureCount]);

  return (
    <AuthShell
      eyebrow="Para nutricionistas"
      headline={<>O seu consultório <span className="l-gradient-text block">num lugar só</span></>}
      tagline="Atendimento, funil e agenda para nutricionistas."
      below={
        <>
          <p>
            Ainda não usa o ChatNutri?{" "}
            <a href={wa(SALES_MESSAGES.login)} target="_blank" rel="noopener noreferrer"
               className="font-semibold text-[var(--l-accent-ink)] hover:underline">Fale com a gente</a>
          </p>
          {SUPPORT_WHATSAPP && (
            <p>
              Problemas para entrar?{" "}
              <a href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer"
                 className="font-semibold text-[var(--l-accent-ink)] hover:underline">Falar com o suporte</a>
            </p>
          )}
        </>
      }
    >
      {forgot ? (
        <ForgotPassword initialEmail={email} onClose={() => setForgot(false)} />
      ) : (
        <>
          {/* Ungendered on purpose. Most users here are nutricionistas, but "bem-vinda" greets a
              man by the wrong name on his own login screen, and there is a warm phrasing that
              does not have to guess. */}
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-[1.75rem]">Que bom ver você de novo</h1>
          <p className="mt-1.5 text-sm text-[var(--l-ink-muted)]">Entre para ver o que precisa de você hoje.</p>

          <form className="mt-7 space-y-5" onSubmit={(e) => { e.preventDefault(); login.mutate({ email, password }); }}>
            <Field id="email" label="E-mail">
              <TextField ref={emailRef} id="email" type="email" autoComplete="email" required inputMode="email"
                         placeholder="voce@consultorio.com.br" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <label htmlFor="password" className="text-sm font-semibold text-[var(--l-ink)]">Senha</label>
                <button type="button" onClick={() => setForgot(true)}
                        className="text-sm font-semibold text-[var(--l-accent-ink)] hover:underline">
                  Esqueci minha senha
                </button>
              </div>
              <PasswordField ref={passwordRef} id="password" autoComplete="current-password" required
                             aria-invalid={error ? true : undefined}
                             value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <FormAlert>{error}</FormAlert>}
            <SubmitButton busy={login.isPending} busyLabel="Entrando…">Entrar</SubmitButton>
          </form>
        </>
      )}
    </AuthShell>
  );
}
